import React, { useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { ChangePasswordPayload } from '../types';
import { useChangePasswordMutation } from '../api/usersApiSlice';
import { showToast } from '../../../components/ui/Toast';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '../../../features/auth/authSlice';

interface ApiError {
  data?: {
    message?: string;
  };
}

export const ChangePasswordForm = () => {
  const currentUser = useSelector(selectCurrentUser);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  const { 
    register, 
    handleSubmit, 
    formState: { errors },
    setError,
    reset
  } = useForm<ChangePasswordPayload & { confirmPassword: string }>({
    defaultValues: {
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    }
  });
  
  const [changePassword, { isLoading }] = useChangePasswordMutation();
  
  const onSubmit: SubmitHandler<ChangePasswordPayload & { confirmPassword: string }> = async (data) => {
    const { oldPassword, newPassword, confirmPassword } = data;
    
    if (newPassword !== confirmPassword) {
      setError('confirmPassword', { 
        type: 'manual', 
        message: 'Las contraseñas no coinciden'
      });
      return;
    }
    
    try {
      await changePassword({
        oldPassword,
        newPassword
      }).unwrap();
      
      setSuccessMessage('Contraseña actualizada correctamente');
      reset();
      
      showToast('Contraseña actualizada correctamente', 'success');
      
      setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
    } catch (err) {
      console.error('Error al cambiar contraseña:', err);
      
      const apiError = err as ApiError;
      if (apiError?.data?.message) {
        setError('oldPassword', {
          type: 'manual',
          message: apiError.data.message
        });
        
        showToast(apiError.data.message, 'error');
      } else {
        setError('oldPassword', {
          type: 'manual',
          message: 'Error al cambiar la contraseña'
        });
        
        showToast('Error al cambiar la contraseña', 'error');
      }
    }
  };
  
  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {/* Campo oculto de usuario para accesibilidad */}
      <input
        type="text"
        id="username"
        autoComplete="username"
        value={currentUser?.email || ''}
        readOnly
        className="sr-only"
        aria-hidden="true"
      />
      
      {successMessage && (
        <div className="bg-success/10 border border-success/30 text-success px-4 py-3 rounded-md mb-4" role="alert">
          <p className="text-sm">{successMessage}</p>
        </div>
      )}
      
      {errors.root?.serverError && (
        <div className="bg-destructive/10 border border-destructive/30 text-destructive px-4 py-3 rounded-md mb-4" role="alert">
          <p className="text-sm">{errors.root.serverError.message}</p>
        </div>
      )}
      
      <div>
        <label htmlFor="oldPassword" className="block text-sm font-medium text-foreground mb-1">
          Contraseña Actual
        </label>
        <input
          id="oldPassword"
          type="password"
          autoComplete="current-password"
          aria-describedby={errors.oldPassword ? "oldPassword-error" : undefined}
          {...register("oldPassword", { 
            required: "La contraseña actual es obligatoria",
            minLength: { value: 6, message: "La contraseña debe tener al menos 6 caracteres" }
          })}
          className={`block w-full px-3 py-2 border ${errors.oldPassword ? 'border-destructive' : 'border-border'} rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary bg-background text-foreground sm:text-sm`}
          disabled={isLoading}
        />
        {errors.oldPassword && (
          <p id="oldPassword-error" className="mt-1 text-sm text-destructive" role="alert">{errors.oldPassword.message}</p>
        )}
      </div>
      
      <div>
        <label htmlFor="newPassword" className="block text-sm font-medium text-foreground mb-1">
          Nueva Contraseña
        </label>
        <input
          id="newPassword"
          type="password"
          autoComplete="new-password"
          aria-describedby={errors.newPassword ? "newPassword-error" : undefined}
          {...register("newPassword", { 
            required: "La nueva contraseña es obligatoria",
            minLength: { value: 6, message: "La contraseña debe tener al menos 6 caracteres" }
          })}
          className={`block w-full px-3 py-2 border ${errors.newPassword ? 'border-destructive' : 'border-border'} rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary bg-background text-foreground sm:text-sm`}
          disabled={isLoading}
        />
        {errors.newPassword && (
          <p id="newPassword-error" className="mt-1 text-sm text-destructive" role="alert">{errors.newPassword.message}</p>
        )}
      </div>
      
      <div>
        <label htmlFor="confirmPassword" className="block text-sm font-medium text-foreground mb-1">
          Confirmar Nueva Contraseña
        </label>
        <input
          id="confirmPassword"
          type="password"
          autoComplete="new-password"
          aria-describedby={errors.confirmPassword ? "confirmPassword-error" : undefined}
          {...register("confirmPassword", { 
            required: "Debes confirmar la nueva contraseña",
            minLength: { value: 6, message: "La contraseña debe tener al menos 6 caracteres" }
          })}
          className={`block w-full px-3 py-2 border ${errors.confirmPassword ? 'border-destructive' : 'border-border'} rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary bg-background text-foreground sm:text-sm`}
          disabled={isLoading}
        />
        {errors.confirmPassword && (
          <p id="confirmPassword-error" className="mt-1 text-sm text-destructive" role="alert">{errors.confirmPassword.message}</p>
        )}
      </div>
      
      <div>
        <button
          type="submit"
          disabled={isLoading}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 text-sm disabled:opacity-50"
        >
          {isLoading ? 'Actualizando...' : 'Actualizar Contraseña'}
        </button>
      </div>
    </form>
  );
}; 